import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAppAuth } from '@/auth/use-app-auth';

interface NotificationContextType {
  hasNewActivities: boolean;
  lastViewedAt: string | null;
  markActivitiesAsViewed: () => void;
  checkForNewActivities: (activities: any[]) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAppAuth();
  const [hasNewActivities, setHasNewActivities] = useState(false);
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(null);

  // Load last viewed timestamp from localStorage
  useEffect(() => {
    if (user?.sub) {
      const stored = localStorage.getItem(`lastViewedActivities_${user?.sub || 'anonymous'}`);
      if (stored) {
        setLastViewedAt(stored);
      }
    }
  }, [user?.sub]);

  // Mark activities as viewed and store timestamp
  const markActivitiesAsViewed = () => {
    const now = new Date().toISOString();
    setLastViewedAt(now);
    setHasNewActivities(false);
    
    if (user?.sub) {
      localStorage.setItem(`lastViewedActivities_${user?.sub || 'anonymous'}`, now);
    }
  };

  // Check if there are new activities since last viewed
  const checkForNewActivities = (activities: any[]) => {
    if (!lastViewedAt || activities.length === 0) {
      setHasNewActivities(activities.length > 0);
      return;
    }

    const lastViewed = new Date(lastViewedAt);
    const hasNewActivity = activities.some(activity => {
      const activityTime = new Date(activity.timestamp || activity.createdAt);
      return activityTime > lastViewed;
    });

    setHasNewActivities(hasNewActivity);
  };

  return (
    <NotificationContext.Provider
      value={{
        hasNewActivities,
        lastViewedAt,
        markActivitiesAsViewed,
        checkForNewActivities,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}; 